/**
 * Post-session recap and the GM's own notes.
 *
 * The recap is generated after the session ends, so this polls while it is
 * pending. GM notes are saved back and become planning context for the next
 * session — which is why they are editable here rather than being a read-only
 * summary the GM has to copy somewhere else.
 */
import { useEffect, useRef, useState } from 'react';

import { getSessionRecap, saveGmNotes } from '@/api/client';
import type { SessionRecap } from '@/api/types';
import { Markdown } from '@/components/Markdown';
import { withoutLeadingH1 } from '@/md/parse';

const POLL_MS = 5_000;

export function RecapCard({ sessionId, presentation }: {
  sessionId: string;
  presentation?: { recap: SessionRecap; notes: string; saveState?: 'idle' | 'saving' | 'saved' };
}) {
  const [localRecap, setRecap] = useState<SessionRecap | null>(null);
  const recap = presentation?.recap ?? localRecap;
  const [localNotes, setNotes] = useState('');
  const notes = presentation?.notes ?? localNotes;
  const [localSaveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const saveState = presentation?.saveState ?? localSaveState;
  // Don't clobber what the GM is typing when a poll lands.
  const notesTouched = useRef(false);

  useEffect(() => {
    if (presentation) return;
    let cancelled = false;
    let timer: number | undefined;

    async function poll(): Promise<void> {
      try {
        const next = await getSessionRecap(sessionId);
        if (cancelled) return;
        setRecap(next);
        if (!notesTouched.current) setNotes(next.gm_notes ?? '');
        if (next.summary_status === 'pending' || next.summary_status === 'not_started') {
          timer = window.setTimeout(() => void poll(), POLL_MS);
        }
      } catch {
        // The session may not have a recap yet; stop rather than loop noisily.
      }
    }

    void poll();
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [sessionId, presentation]);

  if (!recap) return null;

  async function save(): Promise<void> {
    if (presentation) return;
    setSaveState('saving');
    try {
      await saveGmNotes(sessionId, notes);
      setSaveState('saved');
      window.setTimeout(() => setSaveState('idle'), 2000);
    } catch {
      setSaveState('idle');
    }
  }

  return (
    <section className="card">
      <header className="card__head">
        <h2 className="card__title">Session recap</h2>
        <span className="badge">{statusLabel(recap.summary_status)}</span>
      </header>

      {recap.summary_status === 'complete' && recap.summary_md ? (
        <RecapSummary md={recap.summary_md} />
      ) : recap.summary_status === 'failed' ? (
        <div className="notice notice--error">
          The recap couldn’t be written this time. Your session and transcript
          are safe.
        </div>
      ) : (
        <p className="card__body" style={{ color: 'var(--ink-muted)' }}>
          <span className="spinner" aria-hidden="true" /> Writing the recap…
        </p>
      )}

      <label className="field">
        <span className="field__label">Your notes for next time</span>
        <textarea
          className="field__input field__input--multiline"
          rows={4}
          value={notes}
          placeholder="What landed, what to pick up next session, anything you promised the table."
          onChange={(e) => {
            notesTouched.current = true;
            setNotes(e.target.value);
          }}
        />
        <span className="field__hint">
          Carried into the next session as planning context.
        </span>
      </label>

      <button
        type="button"
        className="button"
        onClick={() => void save()}
        disabled={saveState === 'saving'}
      >
        {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved' : 'Save notes'}
      </button>
    </section>
  );
}

export function RecapSummary({ md }: { md: string }) {
  const [copied, setCopied] = useState(false);
  const { summary, suggestedOpening } = splitSuggestedOpening(md);

  async function copyOpening(): Promise<void> {
    if (!suggestedOpening) return;
    try {
      await navigator.clipboard.writeText(suggestedOpening);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2_000);
    } catch {
      // The opening remains selected and readable if clipboard access is off.
    }
  }

  return (
    <>
      {summary ? (
        <div className="card__body recap__summary">
          {/* The recap is Markdown by construction — the generator is told
              "return concise Markdown only" — and this used to split it on
              blank lines into <p> tags, so a GM read literal hash marks and
              asterisks. The leading h1 is dropped because the card header
              two lines up already says "Session recap". */}
          <Markdown md={withoutLeadingH1(summary)} />
        </div>
      ) : null}

      {suggestedOpening ? (
        <section className="recap__opening">
          <header className="recap__opening-head">
            <div>
              <p className="recap__opening-eyebrow">Ready for next time</p>
              <h3>Suggested opening for next session</h3>
            </div>
            <button
              type="button"
              className="button button--quiet"
              onClick={() => void copyOpening()}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </header>
          <Markdown md={suggestedOpening} />
        </section>
      ) : null}
    </>
  );
}

export function splitSuggestedOpening(md: string): {
  summary: string;
  suggestedOpening: string;
} {
  const heading = /^#{2,6}\s+Suggested opening for next session\s*$/im;
  const match = heading.exec(md);
  if (!match || match.index === undefined) {
    return { summary: md.trim(), suggestedOpening: '' };
  }

  const bodyStart = match.index + match[0].length;
  const remainder = md.slice(bodyStart).replace(/^\s+/, '');
  const nextHeading = /^#{1,6}\s+/m.exec(remainder);
  const openingEnd = nextHeading?.index ?? remainder.length;
  const suggestedOpening = remainder.slice(0, openingEnd).trim();
  const before = md.slice(0, match.index).trim();
  const after = remainder.slice(openingEnd).trim();
  return {
    summary: [before, after].filter(Boolean).join('\n\n'),
    suggestedOpening,
  };
}

function statusLabel(status: string): string {
  switch (status) {
    case 'complete':
      return 'Ready';
    case 'pending':
      return 'Generating';
    case 'failed':
      return 'Failed';
    default:
      return 'Not started';
  }
}
